/**
 * Report catalogue — identifiers and routing helpers shared across the
 * Measurement section.
 *
 * The catalogue's *content* (which reports exist, their labels, cadence,
 * latest published period) is served by the backend (`GET /reports`, see
 * `useReports`) and is not duplicated here. This module keeps only the
 * routing-level concerns that stay true regardless of which reports exist.
 */

/** What a report covers, from its latest edition's scope. */
export type ReportKind = "portfolio" | "tenant" | "campaign" | "event";

export const REPORT_KIND_LABELS: Record<ReportKind, string> = {
  portfolio: "Portfolio",
  tenant: "Tenant",
  campaign: "Campaign",
  event: "Protected event",
};

export interface ReportSummary {
  /** Report definition key, used as the route segment (e.g. "protection-overview"). */
  id: string;
  name: string;
  description: string;
  /** How often this report is produced, e.g. "monthly" or "per campaign". */
  cadence: string;
  /** Reporting period of the latest published instance, if any. */
  period: string | null;
  kind: ReportKind;
  /** Human summary of the latest edition's scope, e.g. "Canal+ Africa". */
  scopeLabel: string | null;
  /** Who or what generated the latest edition ("seed:…", "account:…"). */
  generatedBy: string | null;
  /** Protected reports cannot be deleted. */
  protected: boolean;
  /** The current account may delete it (its creator, or an administrator). */
  canDelete: boolean;
  /** The current account may protect or unprotect it (administrators). */
  canProtect: boolean;
}

/**
 * Report that legacy flat campaign/event links (before drill-downs were
 * nested under their report) are redirected to.
 */
export const DEFAULT_REPORT_ID = "protection-overview";

export function reportHref(id: string): string {
  return `/measurement/${encodeURIComponent(id)}`;
}

/** The "new report" journey. */
export const NEW_REPORT_HREF = "/measurement/new";

/**
 * Campaign drill-down (level 2), nested under its report (level 1) so the
 * parent slug is always present in the URL — never `/measurement/campaigns/:id`.
 */
export function campaignHref(reportId: string, campaignId: string): string {
  return `/measurement/${encodeURIComponent(reportId)}/campaigns/${encodeURIComponent(campaignId)}`;
}

/**
 * Protected-event drill-down (level 3), nested under its campaign (level 2)
 * and report (level 1).
 */
export function protectedEventHref(
  reportId: string,
  campaignId: string,
  protectedEventId: string,
): string {
  return `${campaignHref(reportId, campaignId)}/events/${encodeURIComponent(protectedEventId)}`;
}

/** Query parameter naming a report edition other than the latest (its slug). */
export const EDITION_PARAM = "edition";

/** `href` with the current edition kept, so drilling down never switches edition silently. */
export function withEdition(href: string, edition: string | null | undefined): string {
  if (!edition) return href;
  const separator = href.includes("?") ? "&" : "?";
  return `${href}${separator}${EDITION_PARAM}=${encodeURIComponent(edition)}`;
}
